import type { FormEvent } from 'react';
import { forwardRef } from 'react';

import { t } from '@luna/i18n';

import { useImageUploadContext } from '../ImageUploadContext';

export const DEFAULT_ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/tiff'];

interface ImageUploadInputProps {
    acceptedImageTypes?: string[];
}

export const ImageUploadInput = forwardRef<HTMLInputElement, ImageUploadInputProps>(
    (props, ref) => {
        const { acceptedImageTypes = DEFAULT_ACCEPTED_IMAGE_TYPES } = props;
        const { openErrorModal, openResizeModal, setImageUrl } = useImageUploadContext();

        const handleFileInputChange = (event: FormEvent<HTMLInputElement>) => {
            event.preventDefault();

            const { files } = event.target as HTMLInputElement;

            if (!files?.[0]) {
                return;
            }

            const fileReader = new FileReader();

            const resetFileInput = () => {
                (event.target as HTMLInputElement).value = '';
            };

            const handleFileReaderLoadEnd = ({ currentTarget }: ProgressEvent) => {
                const reader = currentTarget as FileReader;

                if (!reader.result) {
                    openErrorModal();
                    return;
                }
                setImageUrl(reader.result.toString());
                openResizeModal();
                resetFileInput();
            };

            fileReader.addEventListener('loadend', handleFileReaderLoadEnd);
            fileReader.readAsDataURL(files[0]);
        };

        return (
            <input
                ref={ref}
                data-cy="image-upload-file-input"
                className="d-none"
                name="file"
                type="file"
                accept={acceptedImageTypes.join(',')}
                aria-label={t('stripe.dropdrown.changeImage')}
                onChange={handleFileInputChange}
            />
        );
    },
);
